// StrategyTab.jsx — DAS-3344
//
// The "Strategy" tab on Portfolio Status: the rules the morning advice follows (with an
// on/off switch), the plan check of this morning's run against the plan figures, the pass
// mark per account, and the ten-year outlook with its editable assumptions.
// Reads GET /strategy/overview (DAS-3343) and GET /strategy/projection (DAS-3342) through
// the shared axios instance (no /api prefix — the instance adds it, DAS-2550).

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, InputNumber, Select, Switch, Table, Tag, Typography, message } from 'antd';
import api from '../functions/api';

const { Title, Text } = Typography;

const FLOW_KIND_LABELS = {
  cash_in: 'money in',
  cash_out: 'money out',
  loan_out: 'loan to someone',
  property_out: 'property sold',
};
const NUMBER_KINDS = ['eur', 'pct', 'int', 'year'];

const eur = (v) => (v === null || v === undefined || Number.isNaN(Number(v))
  ? '–' : Math.round(Number(v)).toLocaleString('en-US'));

const plainValue = (v) => (typeof v === 'number' ? v.toLocaleString('en-US') : (v ?? '–'));

const NotAvailable = ({ testid }) => (
  <Text type="secondary" data-testid={testid}>Not available</Text>
);

const block = { marginBottom: 28 };
const row = { display: 'flex', gap: 12, alignItems: 'baseline', padding: '6px 0', borderBottom: '1px solid #f0f0f0' };
const dot = (ok) => ({
  width: 10, height: 10, borderRadius: '50%', flex: 'none', display: 'inline-block',
  backgroundColor: ok ? '#52c41a' : '#ff4d4f',
});

export const summarySentence = (summary, years) => {
  if (!summary) return '';
  const pct = summary.yearly_pct === null || summary.yearly_pct === undefined
    ? null : summary.yearly_pct * 100;
  const trend = pct === null ? ''
    : `, ${pct < 0 ? 'minus' : 'plus'} ${Math.abs(pct).toFixed(1)}% a year`;
  return `Wealth today ${eur(summary.wealth_start)}. After ${years || 10} years ${eur(summary.real_end)} in today's money${trend}. `
    + `Goal 4, at most minus 1% a year: ${summary.goal4_ok ? 'met' : 'not met'}.`;
};

const RulesBlock = ({ overview, failed, onToggle }) => (
  <div style={block}>
    <Title level={5}>Rules the advice follows</Title>
    {failed ? <NotAvailable testid="strategy-rules-na" /> : (overview.rules || []).map((r) => (
      <div key={r.id} style={row}>
        <Switch size="small" checked={r.active} onChange={(v) => onToggle(r.id, v)}
                data-testid={`strategy-rule-switch-${r.id}`} />
        <span style={{ flex: 1, color: r.active ? undefined : '#999' }}>{r.rule_text}</span>
        <Tag color={r.on_breach === 'blocks' ? 'red' : 'gold'}>{r.on_breach}</Tag>
        {r.number && (
          <Text type="secondary">
            {r.number}{r.number_source === 'plan' ? ' (from the plan figures)' : ''}
          </Text>
        )}
      </div>
    ))}
  </div>
);

const PlanBlock = ({ plan, failed }) => (
  <div style={block}>
    <Title level={5}>
      Your plan against today{plan?.run_date ? ` (morning run of ${plan.run_date})` : ''}
    </Title>
    {failed || !plan ? <NotAvailable testid="strategy-plan-na" /> : (plan.measures || []).map((m) => (
      <div key={m.measure} style={row} data-testid={`strategy-plan-row-${m.measure}`}>
        <span style={dot(m.on_plan)} data-on-plan={m.on_plan ? 'yes' : 'no'} />
        <span style={{ flex: 1 }}><b>{m.title}</b> {m.line}</span>
        {m.plan_label && <Text type="secondary">{m.plan_label}: {plainValue(m.plan_value)}</Text>}
      </div>
    ))}
    <div style={{ marginTop: 8 }}>
      <Text type="secondary">Change the plan figures on the Account page, under Plan and tax numbers.</Text>
    </div>
  </div>
);

const PassmarkBlock = ({ passmarks, failed }) => (
  <div style={block}>
    <Title level={5}>Pass marks</Title>
    {failed ? <NotAvailable testid="strategy-passmarks-na" /> : (passmarks || []).map((p) => (
      <div key={p.account} style={row} data-testid={`strategy-passmark-${p.account}`}>
        <b style={{ minWidth: 140 }}>{p.account}</b>
        <Tag color={p.status === 'PASS' ? 'green' : 'red'}>{p.status}</Tag>
        <span style={{ flex: 1 }}>{p.line}</span>
      </div>
    ))}
  </div>
);

const PensionEditor = ({ value, onChange }) => {
  const entries = Object.entries(value || {});
  const set = (list) => onChange(Object.fromEntries(list.filter(([y]) => String(y).trim() !== '')));
  return (
    <div>
      {entries.map(([year, amount], i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          <InputNumber placeholder="year" value={Number(year)} min={2000} max={2100}
                       onChange={(y) => set(entries.map((e, j) => (j === i ? [String(y ?? ''), e[1]] : e)))} />
          <InputNumber placeholder="amount" value={amount} min={0} style={{ width: 140 }}
                       onChange={(a) => set(entries.map((e, j) => (j === i ? [e[0], a ?? 0] : e)))} />
          <Button size="small" onClick={() => set(entries.filter((_, j) => j !== i))}>remove</Button>
        </div>
      ))}
      <Button size="small" onClick={() => {
        const last = entries.length ? Math.max(...entries.map(([y]) => Number(y))) : new Date().getFullYear();
        set([...entries, [String(last + 1), 0]]);
      }}>add a year</Button>
    </div>
  );
};

const FlowsEditor = ({ value, onChange }) => {
  const flows = value || [];
  const edit = (i, patch) => onChange(flows.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <div>
      {flows.map((f, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <InputNumber placeholder="year" value={f.year} min={2000} max={2100} onChange={(y) => edit(i, { year: y })} />
          <Select value={f.kind} style={{ width: 160 }} onChange={(k) => edit(i, { kind: k })}
                  options={Object.entries(FLOW_KIND_LABELS).map(([k, l]) => ({ value: k, label: l }))} />
          <InputNumber placeholder="amount" value={f.amount_eur} min={0} style={{ width: 140 }}
                       onChange={(a) => edit(i, { amount_eur: a })} />
          <Input placeholder="label" value={f.label} style={{ width: 240 }}
                 onChange={(e) => edit(i, { label: e.target.value })} />
          <Button size="small" onClick={() => onChange(flows.filter((_, j) => j !== i))}>remove</Button>
        </div>
      ))}
      <Button size="small" onClick={() => onChange([...flows, {
        year: new Date().getFullYear() + 1, kind: 'cash_out', amount_eur: 0, label: '',
      }])}>add a flow</Button>
    </div>
  );
};

const PROJECTION_COLUMNS = [
  { title: 'Year', dataIndex: 'year', key: 'year' },
  { title: 'Age', dataIndex: 'age', key: 'age' },
  ...[
    ['Income', 'income'], ['Tax', 'tax'], ['Taken out', 'withdrawal'], ['Savings', 'savings'],
    ['Portfolio', 'portfolio'], ['Loans', 'loans'], ['Home', 'property'], ['Total', 'wealth'],
    ["In today's money", 'real'],
  ].map(([title, key]) => ({ title, dataIndex: key, key, align: 'right', render: eur })),
];

const ProjectionBlock = ({ projection, failed, onSave, saving }) => {
  const [draft, setDraft] = useState(projection?.inputs || {});
  useEffect(() => { setDraft(projection?.inputs || {}); }, [projection]);

  if (failed || !projection) {
    return (
      <div style={block}>
        <Title level={5}>Ten years ahead</Title>
        <NotAvailable testid="strategy-projection-na" />
      </div>
    );
  }
  const specs = projection.specs || [];
  const changed = Object.fromEntries(Object.entries(draft).filter(
    ([k, v]) => JSON.stringify(v) !== JSON.stringify((projection.inputs || {})[k]),
  ));
  const { summary } = projection;

  return (
    <div style={block}>
      <Title level={5}>Ten years ahead</Title>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
        {specs.filter((s) => NUMBER_KINDS.includes(s.kind)).map((s) => (
          <label key={s.key} style={{ display: 'block' }}>
            <div><b>{s.label}</b></div>
            <InputNumber value={draft[s.key]} style={{ width: '100%' }}
                         step={s.kind === 'pct' ? 0.1 : 1}
                         onChange={(v) => setDraft({ ...draft, [s.key]: v })}
                         data-testid={`strategy-input-${s.key}`} />
            <Text type="secondary" style={{ fontSize: 12 }}>{s.help}</Text>
          </label>
        ))}
      </div>
      {specs.filter((s) => s.key === 'pension_schedule').map((s) => (
        <div key={s.key} style={{ marginTop: 16 }}>
          <div><b>{s.label}</b> <Text type="secondary" style={{ fontSize: 12 }}>{s.help}</Text></div>
          <PensionEditor value={draft[s.key]} onChange={(v) => setDraft({ ...draft, [s.key]: v })} />
        </div>
      ))}
      {specs.filter((s) => s.key === 'planned_flows').map((s) => (
        <div key={s.key} style={{ marginTop: 16 }}>
          <div><b>{s.label}</b> <Text type="secondary" style={{ fontSize: 12 }}>{s.help}</Text></div>
          <FlowsEditor value={draft[s.key]} onChange={(v) => setDraft({ ...draft, [s.key]: v })} />
        </div>
      ))}
      <div style={{ margin: '16px 0' }}>
        <Button type="primary" onClick={() => onSave(changed)} loading={saving}
                disabled={Object.keys(changed).length === 0} data-testid="strategy-projection-save">
          Save
        </Button>
      </div>
      {summary ? (
        <>
          <div data-testid="strategy-projection-summary"
               style={{ color: summary.goal4_ok ? '#389e0d' : '#cf1322', fontWeight: 600, marginBottom: 12 }}>
            {summarySentence(summary, (projection.inputs || {}).years)}
          </div>
          <Table size="small" pagination={false} rowKey="year" scroll={{ x: true }}
                 columns={PROJECTION_COLUMNS} dataSource={projection.rows || []} />
        </>
      ) : (
        <Text type="secondary" data-testid="strategy-projection-summary">
          No balances recorded yet; the outlook starts after the first monthly figure.
        </Text>
      )}
    </div>
  );
};

const StrategyTab = () => {
  const [overview, setOverview] = useState(null);
  const [overviewFailed, setOverviewFailed] = useState(false);
  const [projection, setProjection] = useState(null);
  const [projectionFailed, setProjectionFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/strategy/overview')
      .then((res) => { setOverview(res.data || {}); setOverviewFailed(false); })
      .catch(() => setOverviewFailed(true));
    api.get('/strategy/projection')
      .then((res) => { setProjection(res.data || null); setProjectionFailed(false); })
      .catch(() => setProjectionFailed(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleRule = async (id, active) => {
    try {
      const res = await api.put(`/strategy/rules/${id}`, { active });
      setOverview((o) => ({ ...o, rules: res.data?.rules || o.rules }));
      message.success(active ? 'Rule switched on. Tomorrow\'s advice applies it.' : 'Rule switched off. Tomorrow\'s advice skips it.');
    } catch (e) {
      message.error('The rule could not be saved.');
    }
  };

  const saveInputs = async (changed) => {
    try {
      setSaving(true);
      const res = await api.put('/strategy/inputs', changed);
      setProjection(res.data);
      message.success('Saved. The outlook is recalculated.');
    } catch (e) {
      const err = e?.response?.data?.error;
      message.error(Array.isArray(err) ? err.join('; ') : (err || 'The assumptions could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const errors = overview?.errors || [];
  return (
    <div data-testid="strategy-tab-panel" style={{ padding: '8px 4px' }}>
      {!overview && !overviewFailed ? <div style={block}><Text type="secondary">Loading…</Text></div> : (
        <>
          <RulesBlock overview={overview || {}} failed={overviewFailed || errors.includes('rules')} onToggle={toggleRule} />
          <PlanBlock plan={overview?.plan} failed={overviewFailed || errors.includes('plan')} />
          <PassmarkBlock passmarks={overview?.passmarks} failed={overviewFailed || errors.includes('passmarks')} />
        </>
      )}
      {!projection && !projectionFailed ? <div style={block}><Text type="secondary">Loading…</Text></div> : (
        <ProjectionBlock projection={projection} failed={projectionFailed} onSave={saveInputs} saving={saving} />
      )}
    </div>
  );
};

export default StrategyTab;
